'use client';

import { useEffect, useRef, useState } from 'react';
import { nb } from '@/lib/nobreak';

/**
 * The hero's "lead arrived" notification as a live feed (owner, 2026-10-02): a lead slides in at the top right of the
 * funnel card, holds, fades away, and a different one follows (purchase, refinance, VA, jumbo …), so it reads as leads
 * coming in steadily. Clean, not busy: one card at a time, a long calm gap between them.
 *
 * Runs only while the hero is on screen and the tab is visible; pauses while the pointer is over it. The server HTML
 * (and reduced-motion visitors) get the first lead as a still card. Every detail line is page content (frontmatter
 * v2.heroStage.feed); they are illustrative, never counts or claims.
 */
const SHOW = 3800, OUT = 450, GAP = 1100, FIRST = 900;

export function LPLeadFeed({ title, status, when, details }: { title: string; status: string; when: string; details: string[] }) {
  const [i, setI] = useState(0);
  // 'in' = visible, 'out' = fading away, 'pre' = waiting off to the side for its entrance
  const [s, setS] = useState<'in' | 'out' | 'pre'>('in');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || details.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const hero = document.getElementById('lp-hero') ?? el;
    let t = 0, onScreen = true, hover = false, step: 'in' | 'out' | 'pre' = 'in', idx = 0, started = false;
    const clear = () => window.clearTimeout(t);
    const live = () => onScreen && !hover && !document.hidden;
    const run = (wait: number) => {
      clear();
      if (!live()) return;
      t = window.setTimeout(() => {
        if (!live()) return;
        if (step === 'in') { step = 'out'; setS('out'); run(OUT); }
        else if (step === 'out') { step = 'pre'; idx = (idx + 1) % details.length; setI(idx); setS('pre'); run(GAP); }
        else { step = 'in'; setS('in'); run(SHOW); }
      }, wait);
    };
    // the first card fades out after its hold; the cycle starts once the page has settled
    const start = () => { if (started) return; started = true; run(FIRST + SHOW); };
    const resume = () => { if (!started) return start(); run(step === 'in' ? SHOW : step === 'out' ? OUT : GAP); };
    const io = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; if (onScreen) resume(); else clear(); }, { threshold: 0.25 });
    io.observe(hero);
    const vis = () => { if (document.hidden) clear(); else if (onScreen) resume(); };
    const enter = () => { hover = true; clear(); if (step !== 'in') { step = 'in'; setS('in'); } };
    const leave = () => { hover = false; resume(); };
    document.addEventListener('visibilitychange', vis);
    el.addEventListener('pointerenter', enter); el.addEventListener('pointerleave', leave);
    return () => { clear(); io.disconnect(); document.removeEventListener('visibilitychange', vis); el.removeEventListener('pointerenter', enter); el.removeEventListener('pointerleave', leave); };
  }, [details.length]);

  return (
    <div ref={ref} data-s={s} aria-hidden={i > 0 ? true : undefined}
      className="lp-stage-lead lp-lead-feed absolute -top-10 right-0 z-[2] w-[280px] xl:-right-2 xl:w-[300px] rounded-2xl bg-white p-4 text-[#0B0C12] shadow-[0_30px_70px_-20px_rgba(0,0,0,.75)] transition-[opacity,transform] duration-500 ease-out data-[s=out]:-translate-y-2 data-[s=out]:opacity-0 data-[s=pre]:translate-x-8 data-[s=pre]:opacity-0 data-[s=pre]:duration-0 xl:-top-3">
      <div className="flex items-center gap-3">
        <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#ff34a7] text-white">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm0 2v.4l8 5.2 8-5.2V7H4Z" /></svg>
          <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-[#28c840] ring-2 ring-white" />
        </span>
        <div className="min-w-0">
          <p className="text-[14px] font-bold leading-tight">{nb(title)}</p>
          <p className="truncate text-[12px] text-[#6B6F7A]">{details[i]}</p>
        </div>
      </div>
      <div className="lp-lead-feed-status mt-3 hidden items-center justify-between rounded-lg bg-[#F4FBF6] px-3 py-2 text-[12px] xl:flex">
        <span className="font-semibold text-[#15803d]">{status}</span><span className="text-[#6B6F7A]">{when}</span>
      </div>
    </div>
  );
}
